import { isAuthorizationError } from "./errors";
import type { ApolloError } from "@apollo/client";
import type { GraphQLError } from "graphql";

const GATEWAY_AUTH_CODES: ReadonlySet<string> = new Set(["UNAUTHENTICATED", "FORBIDDEN"]);
const AUTH_HTTP_STATUSES: ReadonlySet<number> = new Set([401, 403]);

// In the federated path (client -> gateway -> fay), the gateway wraps a fay 401
// in an HTTP 200 response carrying a GraphQL error with apollo's `extensions.code`
// (UNAUTHENTICATED/FORBIDDEN) and `extensions.response.status` — not jsq's
// `extensions.error_code`, and with no networkError. Recognize that shape directly
// instead of relying on the brittle "401: Unauthorized" message-substring fallback.
function isGatewayAuthError(extensions: GraphQLError["extensions"] | undefined): boolean {
  if (!extensions) {
    return false;
  }
  const { code } = extensions;
  if (typeof code === "string" && GATEWAY_AUTH_CODES.has(code)) {
    return true;
  }
  const { response } = extensions;
  if (response && typeof response === "object" && "status" in response) {
    return typeof response.status === "number" && AUTH_HTTP_STATUSES.has(response.status);
  }
  return false;
}

/**
 * True when an Apollo error means the fay session is dead (expired/invalid JWT).
 *
 * fay returns a 401 for an expired/invalid session JWT; the gateway can surface
 * that as a typed auth GraphQL error, an apollo-wrapped gateway auth error, or a
 * raw network 401/403. Either way the session is dead and retrying won't recover
 * it, so a polling loop must stop — otherwise it keeps hitting fay on every tick
 * with credentials that will keep failing, which is what drives fay's error rate
 * up. Shared by every fay poller (UploadRail, coverage grid) so the stop
 * condition stays identical across them.
 */
export function isFayAuthFailure(error: ApolloError): boolean {
  if (isAuthorizationError(error)) {
    return true;
  }
  // A non-Apollo `Error` can reach this slot (e.g. a thrown read error), so
  // don't assume the ApolloError-only fields are present.
  if (error.graphQLErrors?.some((gqlError) => isGatewayAuthError(gqlError.extensions))) {
    return true;
  }
  const { networkError } = error;
  if (networkError && "statusCode" in networkError) {
    return networkError.statusCode === 401 || networkError.statusCode === 403;
  }
  return false;
}

/**
 * The three distinct terminal refusals fay returns. All three arrive stamped
 * with the same `FORBIDDEN` extensions code, so the message text is the only
 * signal that tells them apart.
 *
 * - `"arena-forbidden"` — the arena is not in the caller's authz set.
 * - `"not-enabled"` — the arena kill switch is off for the arena.
 * - `"no-access"` — the caller lacks `CAN_ACCESS_FAY_AI` in the arena.
 */
export type FayAuthFailureReason = "arena-forbidden" | "not-enabled" | "no-access";

// fay's pinned refusal copy, verbatim from `fay/auth/exceptions.py` and
// `authorize_arena` in the fay repo. Both host apps
// render these words from here, so fay refuses in one voice; if fay's copy
// moves, this map is the one place to follow it.
const FAY_REFUSAL_COPY: Record<FayAuthFailureReason, string> = {
  "arena-forbidden": "Forbidden",
  "not-enabled": "Fay is not enabled for this workspace.",
  "no-access": "You don't have access to Fay.",
};

/**
 * The user-facing message for a refusal, in fay's own pinned words. A host
 * chooses *where* to show this, not what it says. `"arena-forbidden"` maps to
 * fay's bare `Forbidden`; hosts may prefer their generic error copy for that
 * one — it exists so the host can make that call per reason.
 */
export function fayAuthFailureMessage(reason: FayAuthFailureReason): string {
  return FAY_REFUSAL_COPY[reason];
}

/**
 * Which of fay's three terminal refusals this error is, or null when it is not
 * one of them (not an auth failure at all, or an auth failure without pinned
 * refusal copy — e.g. a dead session's 401, where the host's generic error is
 * the right rendering).
 *
 * fay raises every refusal as a starlette `HTTPException`, whose `__str__` is
 * `"<status>: <detail>"` — so the GraphQL message arrives as e.g.
 * `"403: Fay is not enabled for this workspace."`. Match the pinned copy as a
 * substring to tolerate that prefix; the bare-`Forbidden` case matches exactly
 * (with or without the prefix) so unrelated messages that merely contain the
 * word don't false-positive.
 */
export function getFayAuthFailureReason(error: ApolloError): FayAuthFailureReason | null {
  if (!isFayAuthFailure(error)) {
    return null;
  }
  for (const gqlError of error.graphQLErrors ?? []) {
    const { message } = gqlError;
    if (typeof message !== "string" || message.length === 0) {
      continue;
    }
    if (message.includes(FAY_REFUSAL_COPY["not-enabled"])) {
      return "not-enabled";
    }
    if (message.includes(FAY_REFUSAL_COPY["no-access"])) {
      return "no-access";
    }
    if (message === "Forbidden" || message.endsWith(": Forbidden")) {
      return "arena-forbidden";
    }
  }
  return null;
}
